import React, { useContext, useEffect, useState } from "react";
import { Paper, Box, Button, useTheme } from "@mui/material";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  AsideNavigation,
  FlexContainer,
  RelativeContainer,
} from "../../shared/components/styled/Container.styled";
import { PageTitle } from "../../shared/components/styled/Typography.styled";
import RequireAuth from "../../shared/components/guards/RequireAuth";
import MarketingBanner from "../../shared/components/configurator/MarketingBanner";
import {
  BANNER_IMAGE_PAYMENTS_KEY,
  BANNER_URL_PAYMENTS_KEY,
} from "../../shared/constants/Configurator.constants";
import LoanDetailsExpandable from "../../shared/components/LoanDetailsExpandable";
import { useResponsivePadding } from "../../shared/hooks/useResponsivePadding";
import PageLayout from "../../shared/components/PageLayout";
import HorizontalMobileNavigation from "../../shared/components/HorizontalMobileNavigation";
import ActionSnackbar from "../../shared/components/ActionSnackbar";
import { AuthContext } from "../../contexts/AuthContext";
import { NO_LOAN_REGISTERED_WARNING_MESSAGE } from "../../shared/constants/UserProfile.constants";

export default function Payments() {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const { activeLoan, token } = useContext(AuthContext);
  const { isMobile, bannerContainer, contentContainer } = useResponsivePadding();
  const [showLoanDetails, setShowLoanDetails] = useState(false);
  const [noLoanSnackbarOpen, setNoLoanSnackbarOpen] = useState(false);

  const paymentNavigationItems = [
    { to: "new", label: "Make a Payment" },
    { to: "activity", label: "Payment Activity" },
    { to: "methods", label: "Payment Methods" },
  ];

  useEffect(() => {
    if (location.pathname === "/payments") {
      navigate("/payments/new", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  useEffect(() => {
    if (token && activeLoan === "no-loan") {
      setNoLoanSnackbarOpen(true);
    } else {
      setNoLoanSnackbarOpen(false);
    }
  }, [token, activeLoan]);

  const handleNoLoanSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setNoLoanSnackbarOpen(false);
  };

  return (
    <RequireAuth>
      <RelativeContainer width="100%">
        <PageTitle
          variant="h1"
          component="h1"
          color="primary.dark"
          fontSize="3rem"
          display={isMobile ? "none" : "block"}
        >
          Payments
        </PageTitle>
        {!isMobile && <LoanDetailsExpandable />}
      </RelativeContainer>
      <Paper elevation={0}>
        <PageLayout>
          <FlexContainer 
            justifycontent="space-between" 
            alignitems="center" 
            width="100%"
            sx={{ marginBottom: isMobile ? "20px" : 0 }}
          >
            <PageTitle
              variant="h1"
              component="h1"
              color="primary.black"
              fontSize="2rem"
              display={isMobile ? "block" : "none"}
              padding="0 !important"
            >
              Payments
            </PageTitle>
            {isMobile && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => setShowLoanDetails(!showLoanDetails)}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "8px",
                  borderColor: "primary.main",
                  color: "primary.main",
                  fontSize: "0.875rem",
                  padding: "8px 16px",
                  minWidth: "140px",
                  "&:hover": {
                    borderColor: "primary.dark",
                    color: "primary.dark"
                  }
                }}
              >
                {showLoanDetails ? "Hide Details" : "View Loan Details"}
              </Button>
            )}
          </FlexContainer>
          
          {isMobile && showLoanDetails && (
            <Box sx={{ 
              marginBottom: "20px",
              backgroundColor: "background.paper",
              borderRadius: "12px",
              padding: "16px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
            }}>
              <LoanDetailsExpandable expanded={true} />
            </Box>
          )}
          
          <FlexContainer alignitems="flex-start" sx={{flexDirection: {xs: "column", md: "row"}}} >
          {isMobile ? (
            <HorizontalMobileNavigation items={paymentNavigationItems} />
          ) : (
            <AsideNavigation>
              {paymentNavigationItems.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className={
                    location.pathname.includes(`/payments/${item.to}`)
                      ? "active"
                      : ""
                  }
                >
                  {item.label}
                </Link>
              ))}
            </AsideNavigation>
          )}
          <FlexContainer padding={contentContainer} flexgrow="2" width={isMobile ? "100%" : "auto"}>
            <FlexContainer width="100%" flexdirection="column" gap="25px">
              <Outlet />
            </FlexContainer>
          </FlexContainer>
          </FlexContainer>
        </PageLayout>
        <FlexContainer padding={bannerContainer} alignitems="flex-start">
          <MarketingBanner
            elementKey={BANNER_IMAGE_PAYMENTS_KEY}
            hyperlinkElementKey={BANNER_URL_PAYMENTS_KEY}
          />
        </FlexContainer>
      </Paper>
      <ActionSnackbar
        severity="warning"
        borderColor={theme.palette.warning.main}
        vertical="top"
        horizontal="center"
        width="50%"
        isOpen={noLoanSnackbarOpen}
        closeFn={handleNoLoanSnackbarClose}
        snackbarLabel={NO_LOAN_REGISTERED_WARNING_MESSAGE}
        actionLabel="Register Loan"
        actionFn={() => navigate("/profile/loan")}
      />
    </RequireAuth>
  );
}